import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../../AppContext';
import {
  IHistoryAward,
  useAwardCreatedSubscription,
  useAwardDeletedSubscription,
  useAwardUpdatedSubscription,
  useDeleteAwardMutation,
  useGetHistoryAwardsQuery
} from '../../../../generated/graphql';
import { canUserCreate } from '../../../../utils/rightUtils';
import { Actions } from '../../../../enums/ActionEnums';
import { Types } from '../../../../enums/TypeEnums';
import { renderDate } from '../../../../utils/styleUtils';
import { IHeadCell } from '../../../Common/Tables/OverviewTable';
import DenseTable from '../../../Common/Tables/DenseTable';
import { Box } from '@mui/material';
import AwardForm from './AwardForm';
import InnerLoader from '../../../Common/InnerLoader';

interface IProps {
  stockId: string | undefined;
}

const AwardsTab: React.FunctionComponent<IProps> = ({ stockId }: IProps) => {
  const { dispatch, state } = useContext(AppContext);
  const [allData, setAllData] = useState<IHistoryAward[]>([]);
  const [openAwardForm, setOpenAwardForm] = useState(false);
  const [award, setAward] = useState<IHistoryAward | null>(null);
  const canCreate = canUserCreate(state.user, Types.Stock);
  const { data, loading } = useGetHistoryAwardsQuery({
    fetchPolicy: 'no-cache',
    variables: {
      stock_id: parseInt(stockId as string)
    }
  });
  const [deleteAward] = useDeleteAwardMutation();
  const createAwardSubscription = useAwardCreatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const updateAwardSubscription = useAwardUpdatedSubscription({
    variables: { stock_id: stockId || '' }
  });
  const deleteAwardSubscription = useAwardDeletedSubscription({
    variables: { stock_id: stockId || '' }
  });

  useEffect(() => {
    if (data && data.result) {
      setAllData(data.result as IHistoryAward[]);
    }
  }, [data]);

  useEffect(() => {
    if (createAwardSubscription.data?.result) {
      const existing = allData.find((a) => a.id === createAwardSubscription.data?.result.id);
      if (!existing) {
        setAllData([createAwardSubscription.data.result as never, ...allData]);
      }
    }
    if (deleteAwardSubscription.data?.result) {
      removeAward(deleteAwardSubscription.data.result);
    }
    if (updateAwardSubscription.data?.result) {
      const updated = updateAwardSubscription.data?.result as IHistoryAward;
      const data = allData as IHistoryAward[];
      const result = data.map((d) => (d.id === updated.id ? updated : d));
      setAllData(result as never[]);
    }
  }, [createAwardSubscription.data, updateAwardSubscription.data, deleteAwardSubscription.data]);

  const handleClose = (saved: boolean, isNew?: boolean, award?: IHistoryAward) => {
    if (saved && award) {
      if (isNew) {
        setAllData([award as never, ...allData]);
      }
    }

    setAward(null);
    setOpenAwardForm(false);
  };

  const handleDetails = (rowData: IHistoryAward) => {
    setAward(rowData);
    setOpenAwardForm(true);
  };

  const removeAward = (input: IHistoryAward) => {
    const result = (allData as IHistoryAward[]).filter((l) => l.id !== input.id);
    setAllData(result as never[]);
  };

  const handleRemove = (rowData: IHistoryAward) => {
    dispatch({
      type: Actions.ShowConfirmation,
      payload: {
        text: `Are you sure you want to delete Award from ${renderDate(rowData.date)}?`,
        agreeAction: handleDelete,
        params: rowData
      }
    });
  };

  const handleDelete = (awardToDelete: IHistoryAward) => {
    dispatch({
      type: Actions.HideConfirmation
    });
    deleteAward({
      variables: {
        id: awardToDelete.id
      },
      update() {
        removeAward(awardToDelete);
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: `Award from ${renderDate(awardToDelete.date)} deleted`
          }
        });
      }
    }).catch((error) =>
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: error.message,
          autoHide: 6000
        }
      })
    );
  };

  const columns = [
    { title: 'Details', sorting: false, width: 82 },
    {
      title: 'Date Day',
      field: 'date',
      width: 182,
      type: 'date',
      render: (rowData: IHistoryAward) => renderDate(rowData.date)
    },
    { title: 'Award Type ', field: 'type.name', width: 1307 },
    { title: 'Remove', sorting: false, width: 71 }
  ] as IHeadCell[];

  return (
    <Box>
      <Box className="print\:w-full">
        {loading ? (
          <InnerLoader />
        ) : (
          <Box style={{ marginTop: '10px' }}>
            <DenseTable
              columns={columns}
              data={allData}
              loading={loading}
              handleDetails={handleDetails}
              handleRemove={handleRemove}
              createButtonProps={{
                id: 'create-award-button',
                onClick: () => setOpenAwardForm(true),
                text: 'Create',
                disabled: !canCreate
              }}
              maxHeight={1050}
              pageSize={15}
            />
          </Box>
        )}
        {openAwardForm && (
          <AwardForm stockId={stockId as string} awardInput={award} handleClose={handleClose} />
        )}
      </Box>
    </Box>
  );
};

export default AwardsTab;
